import React from 'react';
import {
  Configure,
  Index,
  InstantSearch,
  useHits,
  useInstantSearch,
  useSearchBox,
} from 'react-instantsearch';
import { liteClient as algoliasearch } from 'algoliasearch/lite';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import { format } from 'date-fns';
import { capitalize } from '@mui/material';
import { AppointmentStatus } from '@prisma/client';
import SearchModal, { Props as SearchModalProps } from '../global/SearchModal';
import { API_KEY, APP_ID, INDEX } from '../../algoliaconfig';
import useUser from '../../hooks/global/useUser';
import { LoadedUser } from '../../interfaces';
import { getFullName } from '../../helpers/global';
import { ROUTES } from '../../constants/global';
import useSearchShortcut from '../../hooks/ui/useSearchShortcut';
import useDebouncedValue from '../../hooks/useDebouncedValue';
import useSearchModal from '../../hooks/global/useSearchModal';
import { AppointmentHit } from '../../types/appointments';

const searchClient = algoliasearch(APP_ID, API_KEY);

const getAppointmentStatusText = (status: AppointmentStatus | 'ALL') =>
  `${capitalize(status.toLowerCase())} appointments`;

function Wrapper({ children }: React.PropsWithChildren) {
  const { user } = useUser<LoadedUser>();
  const { appointmentStatus } = useSearchModal();

  const algoliaFilters = React.useMemo(() => {
    const statusString = appointmentStatus
      ? ` AND status:${appointmentStatus}`
      : '';
    const shopFilterString = `businessId:${user?.business.id}${statusString}`;
    const artistFilterString = `employeeId:${user?.userId}${statusString}`;

    if (user?.role === 'EMPLOYEE') {
      return artistFilterString;
    }

    return shopFilterString;
  }, [appointmentStatus, user?.business.id, user?.role, user?.userId]);

  return (
    <InstantSearch
      searchClient={searchClient}
      indexName={INDEX}
      insights
      future={{
        preserveSharedStateOnUnmount: true,
      }}>
      <Index indexName={INDEX}>
        <Configure
          filters={algoliaFilters}
          distinct={false}
          // maximum hits per page is 1000
          hitsPerPage={1000}
          // TODO: Fix customRanking
          // customRanking={
          //   sort
          //     ? JSON.stringify([
          //         `${sort.direction}(${sort.by})`,
          //         'desc(recordUpdatedAt)',
          //       ])
          //     : undefined
          // }
        />
        {children}
      </Index>
    </InstantSearch>
  );
}

function CustomerAppointmentSearchComponent() {
  const {
    isOpen,
    toggle: toggleOpen,
    appointmentsOnly,
    placeholder,
    itemButton,
    appointmentStatus,
    update,
  } = useSearchModal();
  const searchBox = useSearchBox();
  const { status } = useInstantSearch();
  const { results } = useHits();

  const isLoading = useDebouncedValue(status === 'loading', 100);

  const distinctCustomers = React.useMemo(() => {
    const customerIds = new Set<string>();
    return (
      results?.hits?.filter(hit => {
        if (customerIds.has(hit.customerId)) {
          return false;
        }
        customerIds.add(hit.customerId);
        return true;
      }) ?? []
    );
  }, [results?.hits]);

  const appointmentsData = React.useMemo<SearchModalProps['data'][number]>(
    () => ({
      title: 'Appointments',
      results: (
        (results?.hits ?? [])?.map(hit => {
          if (!hit.status) return null;
          const isRequest = ['REQUESTED', 'ACCEPTED', 'REJECTED'].includes(
            hit.status,
          );
          const isComplete = !!hit.checkoutSessionCompleted;
          return {
            type: 'appointment',
            id: hit.objectID,
            title: getFullName({
              firstName: hit.firstName,
              lastName: hit.lastName,
            }),
            icon: isRequest
              ? NotificationsOutlinedIcon
              : CalendarTodayOutlinedIcon,
            details: [
              `Appointment ${
                isComplete ? 'completed' : hit.status?.toLowerCase()
              }`,
              hit.startDateTime
                ? format(new Date(hit.startDateTime), 'EEEE, MMMM do yyyy')
                : `Updated on ${format(
                    new Date(hit.updatedAt),
                    'EEEE, MMMM do yyyy',
                  )}`,
              hit?.serviceCategory
                ? `${
                    hit.tattooLocation ? `${hit.tattooLocation} ` : ''
                  }${capitalize(hit.serviceCategory?.toLowerCase() || '')}`
                : '',
            ].filter(Boolean),
            href: itemButton
              ? undefined
              : isRequest
              ? `${ROUTES.requests}/${hit.objectID}`
              : `${ROUTES.calendar}/${hit.objectID}`,
            itemButton: itemButton
              ? {
                  content: itemButton?.content,
                  onClick: () =>
                    itemButton?.onClick({
                      type: 'appointment',
                      item: hit as unknown as AppointmentHit,
                    }),
                }
              : undefined,
          };
        }) ?? []
      ).filter(Boolean) as SearchModalProps['data'][number]['results'],
    }),
    [itemButton, results?.hits],
  );

  const customerData = React.useMemo<SearchModalProps['data'][number]>(
    () => ({
      title: 'Customers',
      results:
        distinctCustomers?.map(hit => ({
          type: 'customer',
          id: hit.objectID,
          title: getFullName({
            firstName: hit.firstName,
            lastName: hit.lastName,
          }),
          icon: PeopleOutlineIcon,
          href: itemButton
            ? undefined
            : `${ROUTES.customers}/${hit.customerId}`,
          itemButton: itemButton
            ? {
                content: itemButton?.content,
                onClick: () =>
                  itemButton?.onClick({
                    type: 'customer',
                    item: hit as unknown as AppointmentHit,
                  }),
              }
            : undefined,
        })) ?? [],
    }),
    [distinctCustomers, itemButton],
  );

  const data = React.useMemo(() => {
    const tempData = [appointmentsData];
    if (!appointmentsOnly) {
      tempData.push(customerData);
    }
    return tempData;
  }, [appointmentsData, appointmentsOnly, customerData]);

  return (
    <SearchModal
      open={isOpen}
      toggleOpen={toggleOpen}
      onChange={searchBox.refine}
      placeholder={placeholder ?? 'Search for appointments or customers'}
      data={data}
      isLoading={isLoading}
      filter={
        appointmentsOnly
          ? {
              options: (
                ['ALL', 'REQUESTED', 'ACCEPTED', 'SCHEDULED', 'CONFIRMED'] as (
                  | AppointmentStatus
                  | 'ALL'
                )[]
              ).map(s => ({
                label: getAppointmentStatusText(s),
                value: s,
              })),
              selected: appointmentStatus || 'ALL',
              onChange: newStatus =>
                update({
                  appointmentStatus: (newStatus === 'ALL'
                    ? null
                    : newStatus) as AppointmentStatus | null,
                }),
              placeholder: 'Select status',
              defaultValues: ['ALL'],
            }
          : undefined
      }
    />
  );
}

function CustomerAppointmentSearch() {
  const { toggle, isOpen } = useSearchModal();
  const { user } = useUser();

  useSearchShortcut(toggle, !user?.business);

  if (!isOpen || !user?.business) return null;

  return (
    <Wrapper>
      <CustomerAppointmentSearchComponent />
    </Wrapper>
  );
}

export default CustomerAppointmentSearch;
